/**
 * Phase-name matching, for the UI only.
 *
 * This used to live in `suggestedGoalToAnnotation.ts` alongside the client-side
 * suggestion converter. The converter is gone (PR-3 of the coach-input
 * refactor: one backend materialiser, `POST /teaching-videos/{id}/materialize`),
 * and with it `reconcilePhase` / `reconcileGoalScope` — deciding which phase a
 * suggested check belongs to is now the materialiser's job, so that decision
 * can no longer differ between the client and the server.
 *
 * What stays here is the one thing the UI still does on its own:
 * `PhaseReconciliationPanel` compares the names the coach said out loud against
 * the names on the template's phases, so the coach can line them up BEFORE
 * accepting any checks. That is a display comparison, not a scoring decision.
 *
 * Owner decision D8 (2026-10-07): the extractor proposes phase NAMES, order and
 * duration hints — never frame boundaries. The coach sets start and end on the
 * timeline. Nothing in this module produces or guesses a frame number.
 */

/**
 * Normalize a phase name for comparison: lower-case, strip a trailing
 * "phase"/"phases" token, collapse/trim whitespace.
 *
 * LFT-016 lesson: always lower-case at the boundary. A case mismatch
 * ("Lunge" vs "lunge") previously defeated detection filtering, so coaching
 * phase names and template phase labels are compared case-insensitively.
 */
export function normalizePhaseName(name: string): string {
  return name
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .replace(/\s*phases?$/u, '')
    .trim();
}
